// contexts/SubCategoryContext.tsx
import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useCallback,
} from "react";
import { api } from "../services/api";
import toast from "react-hot-toast";
import i18n from "../i18n";
import { useAuth } from "./AuthContext";

export interface SubCategory {
  id: string;
  categoryId: string;
  name: string;
  name_en: string;
  name_th: string;
  name_es: string;
  name_ja: string;
  name_ko: string;
  name_id: string;
  name_ar: string;
  description: string;
  description_th: string;
  description_en: string;
  description_es: string;
  description_ja: string;
  description_ko: string;
  description_id: string;
  description_ar: string;
  prompt: string;
  freeUserPrompt: string;
  icon: string;
  isActive: boolean;
  isDeleted: boolean;
  createdAt: Date;
  updatedAt: Date;
}

interface SubCategoryContextType {
  subCategories: SubCategory[];
  selectedSubCategory: string | null;
  addSubCategory: (
    subCategory: Omit<
      SubCategory,
      "id" | "createdAt" | "updatedAt" | "isDeleted"
    >,
  ) => Promise<void>;
  updateSubCategory: (
    id: string,
    updates: Partial<SubCategory>,
  ) => Promise<void>;
  deleteSubCategory: (id: string) => Promise<void>;
  getSubCategoriesByCategory: (categoryId: string) => Promise<SubCategory[]>;
  getAllSubCategories: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    categoryId?: string;
    isActive?: boolean;
  }) => Promise<{ data: SubCategory[]; meta: any }>;
  setSelectedSubCategory: (subCategoryId: string | null) => void;
  getSubCategoryById: (id: string) => SubCategory | undefined;
  fetchSubCategoriesByCategory: (
    categoryId: string,
    includeInactive?: boolean,
  ) => Promise<SubCategory[]>;
  fetchAllSubCategories: (params?: { isActive?: boolean }) => Promise<void>;
}

const SubCategoryContext = createContext<SubCategoryContextType | undefined>(
  undefined,
);

export const useSubCategories = () => {
  const context = useContext(SubCategoryContext);
  if (!context) {
    throw new Error(
      "useSubCategories must be used within a SubCategoryProvider",
    );
  }
  return context;
};

interface SubCategoryProviderProps {
  children: ReactNode;
}

export const SubCategoryProvider: React.FC<SubCategoryProviderProps> = ({
  children,
}) => {
  const [subCategories, setSubCategories] = useState<SubCategory[]>([]);
  const [selectedSubCategory, setSelectedSubCategory] = useState<string | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [hasFetchedAll, setHasFetchedAll] = useState(false);

  const { user } = useAuth();

  const transformSubCategoryData = (data: any): SubCategory => ({
    id: data._id,
    categoryId: data.categoryId?._id || data.categoryId,
    name: data.name || "",
    name_en: data.name_en || data.name,
    name_th: data.name_th || "",
    name_es: data.name_es || "",
    name_ja: data.name_ja || "",
    name_ko: data.name_ko || "",
    name_id: data.name_id || "",
    name_ar: data.name_ar || "",
    description: data.description || "",
    description_en: data.description_en || data.description || "",
    description_th: data.description_th || "",
    description_es: data.description_es || "",
    description_ja: data.description_ja || "",
    description_ko: data.description_ko || "",
    description_id: data.description_id || "",
    description_ar: data.description_ar || "",
    prompt: data.prompt || "",
    freeUserPrompt: data.freeUserPrompt || "",
    icon: data.icon || "message-square",
    isActive: data.isActive,
    isDeleted: data.isDeleted || false,
    createdAt: new Date(data.createdAt),
    updatedAt: new Date(data.updatedAt),
  });

  const fetchAllSubCategories = useCallback(
    async (params?: { isActive?: boolean }) => {
      if (!user) return;

      try {
        setIsLoading(true);
        // The backend caps `limit` at 100 per page, so a single request can
        // silently truncate the list once there are more than 100 matching
        // subcategories. Page through every page and merge the results so
        // no category's subcategories are dropped from the initial load.
        const pageSize = 100;
        let page = 1;
        let allData: any[] = [];
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const response = await api.get("/backend/subcategories", {
            params: {
              isActive: params?.isActive ?? true,
              limit: pageSize,
              page,
            },
          });

          if (!response.data.success) break;

          allData = allData.concat(response.data.data);

          const totalPages = response.data.meta?.pages ?? 1;
          if (page >= totalPages || response.data.data.length === 0) break;
          page += 1;
        }

        setSubCategories(allData.map(transformSubCategoryData));
        setHasFetchedAll(true);
      } catch (error) {
        console.error("Failed to fetch all subcategories:", error);
        toast.error(i18n.t("messages.subcategoriesLoadFailed"));
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [user],
  );

  const fetchSubCategoriesByCategory = useCallback(
    async (
      categoryId: string,
      includeInactive: boolean = false,
    ): Promise<SubCategory[]> => {
      if (!user) return [];

      try {
        setIsLoading(true);
        const response = await api.get(`/backend/subcategory/${categoryId}`, {
          params: { includeInactive: includeInactive.toString() },
        });

        if (response.data.success) {
          const transformedData = response.data.data.map(
            transformSubCategoryData,
          );
          setSubCategories((prev) => {
            const otherCategories = prev.filter(
              (sc) => sc.categoryId !== categoryId,
            );
            return [...otherCategories, ...transformedData];
          });
          return transformedData;
        }
        return [];
      } catch (error) {
        console.error("Failed to fetch subcategories:", error);
        toast.error(i18n.t("messages.subcategoriesLoadFailed"));
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [user],
  );

  const getAllSubCategories = useCallback(
    async (params?: {
      page?: number;
      limit?: number;
      search?: string;
      categoryId?: string;
      isActive?: boolean;
    }) => {
      try {
        const response = await api.get("/backend/subcategories", { params });

        if (response.data.success) {
          const transformedData = response.data.data.map(
            transformSubCategoryData,
          );
          setSubCategories(transformedData);

          return {
            data: transformedData,
            meta: response.data.meta,
          };
        }
        return { data: [], meta: {} };
      } catch (error) {
        console.error("Failed to fetch all subcategories:", error);
        toast.error(i18n.t("messages.subcategoriesLoadFailed"));
        throw error;
      }
    },
    [],
  );

  // Initialize subcategories when user logs in
  useEffect(() => {
    if (!user) {
      setSubCategories([]);
      setHasFetchedAll(false);
      return;
    }

    if (user && !hasFetchedAll && !isLoading) {
      fetchAllSubCategories({ isActive: true });
    }
  }, [user, hasFetchedAll, isLoading, fetchAllSubCategories]);

  const addSubCategory = async (
    subCategoryData: Omit<
      SubCategory,
      "id" | "createdAt" | "updatedAt" | "isDeleted"
    >,
  ) => {
    try {
      const response = await api.post("/backend/subcategory/add", {
        categoryId: subCategoryData.categoryId,
        name: subCategoryData.name,
        name_en: subCategoryData.name_en,
        name_th: subCategoryData.name_th,
        name_es: subCategoryData.name_es,
        name_ja: subCategoryData.name_ja,
        name_ko: subCategoryData.name_ko,
        name_id: subCategoryData.name_id,
        name_ar: subCategoryData.name_ar,
        description: subCategoryData.description,
        description_en: subCategoryData.description_en,
        description_th: subCategoryData.description_th,
        description_es: subCategoryData.description_es,
        description_ja: subCategoryData.description_ja,
        description_ko: subCategoryData.description_ko,
        description_id: subCategoryData.description_id,
        description_ar: subCategoryData.description_ar,
        prompt: subCategoryData.prompt,
        freeUserPrompt: subCategoryData.freeUserPrompt,
        icon: subCategoryData.icon,
        isActive: subCategoryData.isActive,
      });

      if (response.data.success) {
        const newSubCategory: SubCategory = {
          ...subCategoryData,
          id: response.data.data._id,
          isDeleted: false,
          createdAt: new Date(),
          updatedAt: new Date(),
        };
        setSubCategories((prev) => [...prev, newSubCategory]);
        toast.success("SubCategory added successfully");
      }
    } catch (error: any) {
      console.error("Failed to add subcategory:", error);
      const errorMessage =
        error.response?.data?.message || "Failed to add subcategory";
      toast.error(errorMessage);
      throw error;
    }
  };

  const updateSubCategory = async (
    id: string,
    updates: Partial<SubCategory>,
  ) => {
    try {
      const response = await api.put(
        `/backend/subcategory/${id}/update`,
        updates,
      );

      if (response.data.success) {
        setSubCategories((prev) =>
          prev.map((subCategory) =>
            subCategory.id === id
              ? {
                  ...subCategory,
                  ...updates,
                  updatedAt: new Date(),
                }
              : subCategory,
          ),
        );
        toast.success("SubCategory updated successfully");
      }
    } catch (error: any) {
      console.error("Failed to update subcategory:", error);
      const errorMessage =
        error.response?.data?.message || "Failed to update subcategory";
      toast.error(errorMessage);
      throw error;
    }
  };

  const deleteSubCategory = async (id: string) => {
    try {
      const response = await api.delete(`/backend/subcategory/${id}/delete`);

      if (response.data.success) {
        setSubCategories((prev) =>
          prev.map((subCategory) =>
            subCategory.id === id
              ? {
                  ...subCategory,
                  isDeleted: true,
                  updatedAt: new Date(),
                }
              : subCategory,
          ),
        );
        toast.success("SubCategory deleted successfully");
      }
    } catch (error) {
      console.error("Failed to delete subcategory:", error);
      toast.error("Failed to delete subcategory");
      throw error;
    }
  };

  const getSubCategoriesByCategory = useCallback(
    async (categoryId: string): Promise<SubCategory[]> => {
      const localSubCategories = subCategories.filter(
        (sc) => sc.categoryId === categoryId && !sc.isDeleted,
      );
      if (localSubCategories.length > 0) {
        return localSubCategories;
      }

      const freshlyFetched = await fetchSubCategoriesByCategory(
        categoryId,
        true,
      );
      return freshlyFetched.filter(
        (sc) => sc.categoryId === categoryId && !sc.isDeleted,
      );
    },
    [subCategories, fetchSubCategoriesByCategory],
  );

  const getSubCategoryById = useCallback(
    (id: string) => {
      return subCategories.find(
        (subCategory) => subCategory.id === id && !subCategory.isDeleted,
      );
    },
    [subCategories],
  );

  return (
    <SubCategoryContext.Provider
      value={{
        subCategories: subCategories.filter((sc) => !sc.isDeleted),
        selectedSubCategory,
        addSubCategory,
        updateSubCategory,
        deleteSubCategory,
        getSubCategoriesByCategory,
        getAllSubCategories,
        setSelectedSubCategory,
        getSubCategoryById,
        fetchSubCategoriesByCategory,
        fetchAllSubCategories,
      }}
    >
      {children}
    </SubCategoryContext.Provider>
  );
};
